import { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import useCountdown from '@Hooks/useCountdown';
import { Clock3, Download, AlertCircle, Clock, RefreshCw, Info, Send, CheckCircle, TrendingUp, Search, CheckCircle2, X } from 'lucide-react';
import { Pagination, Skeleton, Stack, Tooltip, Chip, TextField } from '@mui/material';
import ContactDetailsPage from '@CommonFunctionalDisplay/ContactDetailsPage';
import CampaignJobViewService from '@Helpers/URLHelpers/WeboutService/Views/CampaignJobViewService';
import ErrorHandler from '@Root/ErrorHandler';
import './FailedMsgView.scss';

export default function FailedMsgView(props) {

    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [showContact, setShowContact] = useState(false);
    const [campaign, setCampaign] = useState({});
    const [campaignList, setCampaignList] = useState([]);
    const [filterStatus, setFilterStatus] = useState('all');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const size = 10;
    const [campaignJobFilter, setCampaignJobFilter] = useState({
        campaignJobViewFilter: {
            searchQuery: '', campaignRunType: 'whatsapp', campaignRunIdList: [], campaignStatusList: ['failed', 'retry'], retryCountList: [0, 1 ,2 ,3 ,4 ,5, 6]
        },
        page: 0, size
    });
    const [totalPages, setTotalPages] = useState(1);
    const { timeLeft, formatTimer } = useCountdown(stats.nextRetryTimeStamp);
    const campaignListRef = useRef(null);
    const campaignJobViewService = new CampaignJobViewService();
    const errorHandler = new ErrorHandler();

    useEffect(()=> {
        setStats(props?.campaignStatus);
        setCampaign(props?.campaign);
        fetchData();
    },[]);

    useEffect(() => {
        fetchData();
    }, [page, debouncedSearch]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchData = async () => {
        try {
            const apiPage = page - 1;
            const filter = { ...campaignJobFilter };
            filter.campaignJobViewFilter.searchQuery = debouncedSearch;
            filter.campaignJobViewFilter.campaignRunType = props?.campaign.itemType;
            filter.campaignJobViewFilter.campaignRunIdList = [props?.campaign?.id];
            filter.page = apiPage;
            filter.size = size;
            let res = await campaignJobViewService.getListFiltered(filter);
            if (res.status < 400) {
                res = JSON.parse(res.body);
                setCampaignList(res.campaignJobViewList);
                setTotalPages(res.totalPages);
            }
        } catch (err) { errorHandler.handleError(err); }
        setLoading(false);
    };

    const handleSelectStatus = async(value, status)=> {
        try {
            const apiPage = page - 1;
            const filter = { ...campaignJobFilter };
            filter.campaignJobViewFilter.searchQuery = debouncedSearch;
            filter.campaignJobViewFilter.retryCountList = [...value];
            filter.campaignJobViewFilter.campaignStatusList = [...status];
            filter.campaignJobViewFilter.campaignRunType = props?.campaign.itemType;
            filter.campaignJobViewFilter.campaignRunIdList = [props?.campaign?.id];
            filter.page = apiPage;
            filter.size = size;
            let res = await campaignJobViewService.getListFiltered(filter);
            if (res.status < 400) {
                res = JSON.parse(res.body);
                setCampaignList(res.campaignJobViewList);
                setTotalPages(res.totalPages);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
    }

    const getStatusConfig = (status) => {
        switch (status) {
            case 'sent': return { icon: <Send size={13} />, cls: 'badge--sent', label: 'Sent' };
            case 'delivered': return { icon: <CheckCircle size={13} />, cls: 'badge--delivered', label: 'Delivered' };
            case 'read': return { icon: <TrendingUp size={13} />, cls: 'badge--opened', label: 'Read' };
            case 'retry': return { icon: <RefreshCw size={13} />, cls: 'badge--retry', label: 'Retry' };
            case 'queued':
            case 'created' : return { icon: <Clock3 size={13} />, cls: 'badge--queued', label: 'In Queue' };
            case 'failed':
            case 'error': return { icon: <AlertCircle size={13} />, cls: 'badge--failed', label: 'Failed' };
            default: return { icon: <Clock size={13} />, cls: 'badge--pending', label: 'Pending' };
        }
    };

    const getCampaignStatusConfig = (status) => {
        const map = {
            completed: { label: 'Completed', cls: 'run-badge--completed' },
            running: { label: 'Running', cls: 'run-badge--running' },
            scheduled: { label: 'Scheduled', cls: 'run-badge--scheduled' },
            deleted: { label: 'Deleted', cls: 'run-badge--deleted' },
            paused: { label: 'Paused', cls: 'run-badge--paused' },
            queued: { label: 'Queued', cls: 'run-badge--queued' },
            failed: { label: 'Failed', cls: 'run-badge--failed' },
        };
        return map[status] || { label: status, cls: 'run-badge--default' };
    };


    const fmt = (ts) => ts
        ? new Intl.DateTimeFormat('en-US', {
            year: 'numeric', month: 'short', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        }).format(new Date(ts))
        : '—';

    const avatarStyle = (name = '') => {
        const p = [
            { bg: '#DBEAFE', color: '#1D4ED8' },
            { bg: '#DCFCE7', color: '#15803D' },
            { bg: '#FEF9C3', color: '#A16207' },
            { bg: '#EDE9FE', color: '#6D28D9' },
            { bg: '#FCE7F3', color: '#BE185D' },
            { bg: '#FFEDD5', color: '#C2410C' },
        ];
        return p[(name.charCodeAt(0) || 0) % p.length];
    };

    const RowSkeleton = () => (
        <Stack spacing={1.5}>
            {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: '8px' }} />
            ))}
        </Stack>
    );

    const failed = stats.failedCount ?? 0;
    const retry = stats.retryCount ?? 0;
    const completedRetry = stats.completedRetry ?? 0;
    const failedRetry = stats.failedRetry ?? 0;
    const completedOneRetry = stats.completedOneRetry ?? 0;
    const completedTwoRetry = stats.completedTwoRetry ?? 0;
    const completedThreeRetry = stats.completedThreeRetry ?? 0;
    const completedFourRetry = stats.completedFourRetry ?? 0;
    const completedFiveRetry = stats.completedFiveRetry ?? 0;
    const completedSixRetry = stats.completedSixRetry ?? 0;

    const total =
        (stats.sentCount ?? 0) +
        (stats.deliveredCount ?? 0) +
        (stats.readCount ?? 0) +
        failed;

    const successRate =
        total > 0
            ? Math.round(
                (
                    (
                        (stats.deliveredCount ?? 0) +
                        (stats.readCount ?? 0)
                    ) / total
                ) * 100
            )
            : 0;

    const completedRetries = [
        completedOneRetry,
        completedTwoRetry,
        completedThreeRetry,
        completedFourRetry,
        completedFiveRetry,
        completedSixRetry,
    ];

    const handleCloseContactView = ()=> {
        setShowContact(false);
    }

    const handleOpenContactView = (selectedChat)=> {
        setSelectedContact((prev)=> selectedChat);
        setShowContact(true);
    }

    const getError = (item)=> {
        const details = item.campaignJobDetails ? JSON.parse(item.campaignJobDetails) : {};
        const sc = getStatusConfig(item.campaignStatus);
        const showTip = sc.label === 'Failed' || sc.label === 'Retry' || sc.label === 'Unsubscribed';
        return (
            <div className="whatsapp-campaign-status__col-error">
                {showTip && (details?.error ?? 'Error')}
            </div>
        )
    }

    const getItemName = (item) => {
        const av = avatarStyle(item.name);
        return (
            <div className="whatsapp-campaign-status__col-recipient whatsapp-campaign-status__recipient">
                <button
                    type="button"
                    className="whatsapp-campaign-status__contact-link"
                    onClick={() => handleOpenContactView(item)}
                >
                    <div
                        className="whatsapp-campaign-status__avatar"
                        style={{ background: av.bg, color: av.color }}
                    >
                        {(item.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="whatsapp-campaign-status__recipient-info">
                        <span className="whatsapp-campaign-status__recipient-name">{item.name}</span>
                        <span className="whatsapp-campaign-status__recipient-phone">{item.contactNumber}</span>
                    </div>
                </button>
            </div>
        )
    }

    const getCreateDate = (row)=> {
        let lead = row;
        return (
            <div className="whatsapp-campaign-status__col-time">
               {fmt(lead.modifiedTimeStamp)}
            </div>
        );
    }

    const getItemStatus = (row) => {
        const item = row;
        const details = item.campaignJobDetails ? JSON.parse(item.campaignJobDetails) : {};
        const sc = getStatusConfig(item.campaignStatus);
        const showTip = sc.label === 'Failed' || sc.label === 'Retry' || sc.label === 'Unsubscribed';
        const label = sc.label === 'Retry' ? (timeLeft.completed ? 'Running' : sc.label) : sc.label;
        const retryCount = sc.label === 'Retry' ? item.retryCount : 0;
        return (
            <div className="whatsapp-campaign-status__col-status">
                {showTip ? (
                    <Tooltip title={details?.error ?? 'Error'} placement="top" arrow>
                        <span className={`whatsapp-campaign-status__badge ${sc.cls}`}>
                            {sc.icon}{label}
                            {retryCount > 0 &&
                                <span className="whatsapp-campaign-status__retry-count">#{retryCount}</span>}
                        </span>
                    </Tooltip>
                ) : (
                    <span className={`whatsapp-campaign-status__badge ${sc.cls}`}>
                        {sc.icon}{sc.label}
                    </span>
                )}
            </div>
        );
    };

    const getFooter = ()=> {
        return (
            totalPages > 1 &&
                <div className="whatsapp-campaign-status__pagination">
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        shape="rounded"
                        size="small"
                    />
                </div>
        )
    }

    const emptyList = ()=> {
        return (
            <div className="failed-msg-view__empty">
                <div className="failed-msg-view__empty-icon">
                    <CheckCircle2 size={26} />
                </div>
                <h3>No messages found</h3>
                <p>There are no messages matching the current filter.</p>
                <button
                    type="button"
                    onClick={() => {
                            handleSelectStatus([0, 1, 2, 3, 4, 5, 6], ['failed', 'retry']);
                            setFilterStatus('all');
                        }
                    }
                >
                    View failed messages
                </button>
            </div>
        )
    }

    return (
        <div className="failed-msg-view">
            <div className="failed-msg-view__header">
                <div className="failed-msg-view__header-left">
                    <div className="failed-msg-view__header-icon">
                        <RefreshCw size={18} />
                    </div>
                    <div>
                        <h2>Retry Activity</h2>
                        <p>Review delivery attempts and message recovery activity</p>
                    </div>
                </div>
                <div className="failed-msg-view__campaign">
                    <span className="failed-msg-view__campaign-label">
                        Campaign
                    </span>
                    <span className="failed-msg-view__campaign-name">
                        {campaign?.name || 'Current campaign'}
                    </span>
                </div>
            </div>
            <div className="failed-msg-view__panel">
                <div className="failed-msg-view__panel-header">
                    <div className="failed-msg-view__search">
                        <Search size={15} />
                        <input
                            type="text"
                            value={search}
                            placeholder="Search name or number"
                            onChange={(e) =>
                                setSearch(e.target.value)
                            }
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch('')}
                                aria-label="Clear search"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="failed-msg-view__filter-bar">
                    <div className="failed-msg-view__filter-title">
                        <span>FILTER BY</span>
                    </div>
                    <button
                        type="button"
                        className={"failed-msg-view__filter failed-msg-view__filter--failed " + (filterStatus === 'all' ? 'failed-msg-view__filter--failed__active' : '')}
                        onClick={() => {
                                handleSelectStatus([0, 1, 2, 3, 4, 5, 6], ['failed', 'retry']);
                                setFilterStatus('all');
                            }
                        }
                    >
                        <RefreshCw size={13} />
                        <span>All</span>
                        <b>{failedRetry + completedRetry + retry}</b>
                    </button>
                    <button
                        type="button"
                        className={"failed-msg-view__filter failed-msg-view__filter--recovered " + (filterStatus === 'recovered' ? 'failed-msg-view__filter--recovered__active' : '')}
                        onClick={() => {
                                handleSelectStatus([1, 2, 3, 4, 5, 6], ['sent', 'read', 'delivered']);
                                setFilterStatus('recovered');
                            }
                        }
                    >
                        <CheckCircle2 size={13} />
                        <span>Recovered</span>
                        <b>{completedRetry}</b>
                    </button>
                    {completedRetries.map((count, index) => {
                        const retryNumber = index + 1;
                        if (count <= 0) return null;
                        return (
                            <button
                                type="button"
                                key={retryNumber}
                                className={"failed-msg-view__filter failed-msg-view__filter--retry " + (filterStatus === ('recovered__' + retryNumber) ? 'failed-msg-view__filter--retry__active' : '')}
                                onClick={() => {
                                        handleSelectStatus([retryNumber], ['sent', 'read', 'delivered']);
                                        setFilterStatus('recovered__' + retryNumber);
                                    }
                                }
                            >
                                <RefreshCw size={13} />
                                <span>
                                    Retry #{retryNumber}
                                </span>
                                <b>{count}</b>
                            </button>
                        );
                    })}
                    {
                        retry > 0 &&
                            <button
                                type="button"
                                className={"failed-msg-view__filter failed-msg-view__filter--retry " + (filterStatus === 'retry' ? 'failed-msg-view__filter--retry__active' : '')}
                                onClick={() => {
                                        handleSelectStatus([], ['retry']);
                                        setFilterStatus('retry');
                                    }
                                }
                            >
                                <RefreshCw size={13} />
                                <span>Retry</span>
                                <b>{retry}</b>
                            </button>
                    }
                    <button
                        type="button"
                        className={"failed-msg-view__filter failed-msg-view__filter--failed"}
                        onClick={() => {
                                handleSelectStatus([0, 1, 2, 3, 4, 5, 6], ['failed', 'retry']);
                                setFilterStatus('all');
                            }
                        }
                    >
                        <X size={13} />
                        <span>Clear</span>
                    </button>
                </div>
                <div className="failed-msg-view__table">
                    <DataTable
                        value={campaignList}
                        ref={campaignListRef}
                        scrollable
                        scrollHeight="flex"
                        style={{ height: '100%' }}
                        emptyMessage={emptyList}
                        footer={getFooter}
                        dataKey="rn"
                        rowHover
                    >
                        <Column
                            field="recipient"
                            header="Recipient"
                            body={getItemName}
                        />

                        <Column
                            field="error"
                            header="Error"
                            body={getError}
                        />

                        <Column
                            field="status"
                            header="Status"
                            body={getItemStatus}
                        />

                        <Column
                            field="createDate"
                            header="Sent at"
                            body={getCreateDate}
                        />
                    </DataTable>
                </div>
            </div>

            {showContact && (
                <ContactDetailsPage
                    reloadData={() => fetchData()}
                    nameCardId={selectedContact?.nameCardId}
                    onClose={handleCloseContactView}
                />
            )}
        </div>
    );
}